import type { AssetId } from '../../assets/AssetTypes';
import { HAZARDS_CONFIG, type HazardsConfig } from '../../config/hazards.config';
import type { LevelConfig } from '../../config/level.config';
import type { CollectibleCatalog } from '../../collectibles/CollectibleCatalog';
import { CollectibleType } from '../../collectibles/CollectibleType';
import type { DifficultyManager } from '../../difficulty/DifficultyManager';
import { collectibleFootprints, hazardRequirements, type CollectibleFootprint, type LaneStretch } from '../../hazards/hazardRules';
import { ObstacleCatalog } from '../../obstacles/ObstacleCatalog';
import type { ObstacleDefinition } from '../../obstacles/ObstacleDefinition';
import { ObstacleType } from '../../obstacles/ObstacleType';
import { Random } from '../../utils/Random';
import { ROADSIDE_TAG } from '../segments/segmentBuilders';
import type { CollectibleSpawnPoint, DecorationSocket, FixedObstaclePlacement, ObstacleSpawnPoint, ObstacleVariant, SegmentDefinition } from '../segments/SegmentDefinition';
import { DEFAULT_LAYOUT_RULES, type LayoutRules } from '../terrain/LayoutRules';
import {
  dropZoneStretch,
  hazardStretches,
  moduleStretches,
  obstacleFootprint,
  obstacleSpan,
  obstacleStretch,
  roofClimbOf,
  runUpStretch,
  stretchesOverlap,
  terrainStretches,
  type RoofClimb,
} from '../terrain/roofRunUp';
import { analyzeTerrain, type TerrainAnalysis } from '../terrain/terrainAnalysis';
import { terrainRowIndexAt } from '../validation/layoutRows';
import { mergeRowLanes, SequenceValidator, type RowHistory } from '../validation/SequenceValidator';
import type { DecorationDefinition } from './DecorationCatalog';
import { GUIDE_TAG, guideYaw } from './guideSigns';
import { LaneRunUps } from './LaneRunUps';
import { roofLinePoints } from './roofLines';
import type { CollectiblePlacement, DecorationPlacement, ObstaclePlacement, SpawnPlan } from './SpawnPlan';

export interface PopulateContext {
  readonly startDistance: number;
  /** Safe segments (run start) get no obstacles. */
  readonly safe: boolean;
  /** Gameplay placement (obstacles, collectibles). */
  readonly rng: Random;
  /** Decoration only – never shared with gameplay. */
  readonly cosmeticRng: Random;
  /** The segment starts at a junction exit – its first roadside bays would stand in the junction's corner. */
  readonly afterJunction?: boolean;
  /**
   * The module that follows this segment (the TrackManager picks it one module ahead): a container at the end of
   * this segment stands only where its drop zone keeps clear of that module's terrain, portal and moving hazards.
   */
  readonly next?: SegmentDefinition;
  /** Nothing is known beyond the end of this segment (last preview module of an intersection branch): drop zones end inside it. */
  readonly nextUnknown?: boolean;
}

export interface SegmentPopulatorDeps {
  readonly obstacles: ObstacleCatalog;
  readonly collectibles: CollectibleCatalog;
  readonly decorations: readonly DecorationDefinition[];
  readonly difficulty: DifficultyManager;
  readonly validator: SequenceValidator;
  readonly level: LevelConfig;
  /** Physics-derived rules for reading segment terrain (default: shipped configuration). */
  readonly layout?: LayoutRules;
  /** Moving hazard tuning – where hazards keep bottles out (default: shipped configuration). */
  readonly hazards?: HazardsConfig;
  /**
   * Cosmetic pick-weight multiplier of a decoration for a segment starting at `distance` – e.g. more
   * light masts and work lights in night zones (SEG_22). Default: 1. Uses the cosmetic stream only.
   */
  readonly decorationWeight?: (assetId: AssetId, distance: number) => number;
}

/**
 * What the populator carries from segment to segment – saved and restored per branch of an intersection
 * (TrackManager): the SequenceValidator's last row, the busy and reserved stretches of the lanes (run-ups,
 * drop zones) and the pick share owed to obstacles with a roof to climb (see pickDefinition).
 */
export interface SequenceHistory {
  readonly rows: RowHistory | null;
  readonly runUps: LaneRunUps;
  /** Per obstacle id: expected number of placements still owed (negative: placed ahead of its share). */
  readonly owedRoofs: OwedRoofs;
}

/** Per obstacle id with a roof to climb: expected placements still owed. */
export type OwedRoofs = Readonly<Record<string, number>>;

interface PlacedObstacle {
  readonly point: ObstacleSpawnPoint;
  readonly definition: ObstacleDefinition;
}

/** Obstacles of a line and what accepting them does to the owed shares. */
interface RolledRow {
  readonly obstacles: PlacedObstacle[];
  readonly owed: OwedRoofs;
}

/** A bottle of a line with the point it stands on (segment-local). */
interface PlacedBottle {
  readonly point: CollectibleSpawnPoint;
  readonly placement: CollectiblePlacement;
}

/** How the obstacles of a decision line are chosen. */
type SlotMode = 'random' | 'setPiece' | 'variant' | 'terrain' | 'wallEnd' | 'hazard';

/** One decision line of a segment: terrain requirements plus (random or designed) obstacles. */
interface Slot {
  readonly z: number;
  terrain: readonly (ObstacleType | null)[] | null;
  mode: SlotMode;
  /** random: candidate spawn points of the row. */
  points: readonly ObstacleSpawnPoint[];
  /** setPiece / variant: the designed obstacles of the line. */
  fixed: readonly PlacedObstacle[];
  /** hazard: key line of a moving hazard and what it requires per lane. */
  hazard?: { readonly id: string; readonly lanes: readonly (ObstacleType | null)[] };
}

/** Obstacles and moving hazards chosen for a segment. */
interface ObstacleLayout {
  readonly placed: PlacedObstacle[];
  readonly hazards: string[];
  /** Collectible lines the chosen obstacle variant asks for (undefined: any line). */
  readonly lines?: readonly string[];
}

/** Clearance (meters) kept between collectibles and obstacles that cannot be collected over. */
const COLLECTIBLE_CLEARANCE = 1;
/** Bottle arcs over jump obstacles begin this far before the obstacle and end as far behind it. */
const JUMP_ARC_RUN_UP = 2.5;
/** Random stream id of the turbo crates (Random.deriveSeed) – independent of the track's own draws. */
const TURBO_CRATE_STREAM = 0x7c4a7e;

/**
 * Decides what goes into the sockets of a freshly spawned segment.
 * Obstacle rows are validated by the SequenceValidator across segment
 * boundaries (the row history is kept between calls). Terrain (pits, low
 * ceilings, walls) enters the history as rows of its own, so obstacles never
 * crowd a pit or a crossbar in an unplayable way.
 *
 * Obstacles with a walkable top that a double jump gets onto (containers) stand
 * only where their lane offers the free run-up and drop zone of
 * level/terrain/roofRunUp.ts – across segment boundaries (LaneRunUps); elsewhere
 * another obstacle of the same type takes the spot, so the rows stay as hard as
 * they were, and the container's share is handed over to the next spot that fits
 * (it keeps its catalog share of its type). Nothing placed later stands in a
 * container's drop zone, and a drop zone never reaches terrain, a portal or a
 * moving hazard of the next module (PopulateContext.next – the TrackManager picks
 * it one module ahead). Most containers carry a bottle line on the roof
 * (roofLines.ts, LevelConfig.roofRoutes).
 */
export class SegmentPopulator {
  private history: RowHistory | null = null;
  private runUps = LaneRunUps.EMPTY;
  private owedRoofs: OwedRoofs = {};
  private readonly terrain = new Map<string, TerrainAnalysis>();
  private readonly terrainBusy = new Map<string, readonly LaneStretch[]>();
  /** Per module: what is no plain road for a runner dropping into it (moduleStretches, segment-local, cached). */
  private readonly moduleBusy = new Map<string, readonly LaneStretch[]>();
  /** Beyond the end of the segment being populated (run distance): the next module's non-plain stretches, or all of it if unknown. */
  private beyond: readonly LaneStretch[] = [];
  private readonly climbs = new Map<string, RoofClimb | null>();
  /** How far back (m) a run-up check looks from the start of a segment – longer busy stretches are forgotten. */
  private runUpMemory: number | null = null;
  private readonly collectibleLines = new Map<string, readonly string[]>();
  /** Last asset placed in a roadside bay per side – the next bay picks another one. */
  private readonly lastBayAsset = new Map<DecorationSocket['side'], AssetId>();

  constructor(private readonly deps: SegmentPopulatorDeps) {}

  reset(): void {
    this.history = null;
    this.runUps = LaneRunUps.EMPTY;
    this.owedRoofs = {};
    this.lastBayAsset.clear();
  }

  /** Row history of the sequence validator, lane stretches and owed shares – saved/restored per branch of an intersection. */
  get sequenceHistory(): SequenceHistory {
    return { rows: this.history, runUps: this.runUps, owedRoofs: this.owedRoofs };
  }

  set sequenceHistory(history: SequenceHistory | null) {
    this.history = history?.rows ?? null;
    this.runUps = history?.runUps ?? LaneRunUps.EMPTY;
    this.owedRoofs = history?.owedRoofs ?? {};
  }

  populate(definition: SegmentDefinition, ctx: PopulateContext): SpawnPlan {
    // Terrain is geometry: it takes run-ups away whether the segment carries obstacles or not.
    this.runUps = this.runUps
      .forgetBefore(ctx.startDistance - this.lookBack())
      .with(this.terrainBusyOf(definition).map((s) => shifted(s, ctx.startDistance)));
    this.beyond = this.beyondOf(definition, ctx);
    const { placed, hazards, lines } = this.placeObstacles(definition, ctx);
    const occupiedByRow = new Map<string, Set<number>>();
    for (const p of placed) occupiedByRow.set(p.point.row, (occupiedByRow.get(p.point.row) ?? new Set()).add(p.point.lane));
    const laneCount = definition.lanes.length;
    return {
      obstacles: placed.map<ObstaclePlacement>((p) => ({
        spawnPointId: p.point.id,
        obstacleId: p.definition.id,
        yaw: p.definition.tags.includes(GUIDE_TAG) ? guideYaw(p.point.lane, laneCount, occupiedByRow.get(p.point.row) ?? new Set()) : undefined,
      })),
      collectibles: [...this.placeCollectibles(definition, placed, hazards, ctx, lines), ...fixedCollectiblesOf(definition, ctx)],
      decorations: this.placeDecorations(definition, ctx),
      hazards,
    };
  }

  /** Terrain rows and wall stretches of a segment (cached – definitions are immutable). */
  terrainOf(definition: SegmentDefinition): TerrainAnalysis {
    let analysis = this.terrain.get(definition.id);
    if (!analysis) {
      analysis = analyzeTerrain(definition, this.layout);
      this.terrain.set(definition.id, analysis);
    }
    return analysis;
  }

  /** Climb onto an obstacle's walkable top that needs a free run-up, or null (cached per obstacle). */
  roofClimbOf(definition: ObstacleDefinition): RoofClimb | null {
    let climb = this.climbs.get(definition.id);
    if (climb === undefined) {
      climb = roofClimbOf(definition, this.layout);
      this.climbs.set(definition.id, climb);
    }
    return climb;
  }

  private get layout(): LayoutRules {
    return this.deps.layout ?? DEFAULT_LAYOUT_RULES;
  }

  // ── Free run-ups (obstacles with a walkable top) ─────────────────────────

  /** Stretches of the segment's lanes where its terrain is no plain road (segment-local, cached). */
  /**
   * What lies beyond the end of the segment (run distance) for the drop zones of its containers: the next
   * module's terrain, portal and moving hazards – or, if it is not known yet, the whole road (zones end inside).
   */
  private beyondOf(definition: SegmentDefinition, ctx: PopulateContext): readonly LaneStretch[] {
    const end = ctx.startDistance + definition.length;
    if (ctx.nextUnknown) return definition.lanes.map((lane) => ({ lane: lane.index, zFrom: end, zTo: Infinity }));
    const next = ctx.next;
    if (!next) return [];
    let stretches = this.moduleBusy.get(next.id);
    if (!stretches) {
      stretches = moduleStretches(next, this.layout, this.deps.hazards ?? HAZARDS_CONFIG);
      this.moduleBusy.set(next.id, stretches);
    }
    return stretches.map((s) => shifted(s, end));
  }

  private terrainBusyOf(definition: SegmentDefinition): readonly LaneStretch[] {
    let stretches = this.terrainBusy.get(definition.id);
    if (!stretches) {
      stretches = terrainStretches(definition, this.layout);
      this.terrainBusy.set(definition.id, stretches);
    }
    return stretches;
  }

  /** The longest look back of a run-up check from a segment start: run-up plus footprint of the longest climb. */
  private lookBack(): number {
    if (this.runUpMemory === null) {
      this.runUpMemory = 0;
      for (const definition of this.deps.obstacles.all()) {
        const climb = this.roofClimbOf(definition);
        if (climb) this.runUpMemory = Math.max(this.runUpMemory, climb.runUp - obstacleSpan(definition).front);
      }
    }
    return this.runUpMemory;
  }

  /** The obstacle would stand in the drop zone behind a container placed before. */
  private inDropZone(definition: ObstacleDefinition, point: ObstacleSpawnPoint, startDistance: number): boolean {
    return this.runUps.isReserved(obstacleFootprint(definition, point.lane, startDistance + point.position.z));
  }

  /**
   * The obstacle may stand there: out of every drop zone, and – with a roof to climb – its lane offers the free
   * run-up before it and the free drop zone behind it (nothing busy there: terrain, moving hazards, obstacles).
   */
  private fits(definition: ObstacleDefinition, point: ObstacleSpawnPoint, startDistance: number): boolean {
    if (this.inDropZone(definition, point, startDistance)) return false;
    const climb = this.roofClimbOf(definition);
    if (!climb) return true;
    const z = startDistance + point.position.z;
    const drop = dropZoneStretch(definition, climb, point.lane, z);
    return this.runUps.isFree(runUpStretch(definition, climb, point.lane, z)) && this.runUps.isFree(drop) && !this.beyond.some((s) => stretchesOverlap(s, drop));
  }

  /**
   * A designed obstacle that cannot stand there: a container whose run-up or drop zone is taken becomes another
   * obstacle of its type without a roof (the row stays exactly as hard); anything in the drop zone of a container
   * before it is left out (the lane stays free). A container that does not come is owed to the random rows.
   */
  private designedObstacle(placed: PlacedObstacle, ctx: PopulateContext, owed: Record<string, number>): PlacedObstacle | null {
    if (this.fits(placed.definition, placed.point, ctx.startDistance)) return placed;
    if (this.roofClimbOf(placed.definition)) owed[placed.definition.id] = (owed[placed.definition.id] ?? 0) + 1;
    if (this.inDropZone(placed.definition, placed.point, ctx.startDistance)) return null;
    const others = this.deps.obstacles.randomOfType(placed.definition.type).filter((d) => !this.roofClimbOf(d));
    const substitute = ctx.rng.weighted(others.map((d) => [d, d.weight] as const));
    return substitute ? { point: placed.point, definition: substitute } : null;
  }

  /**
   * One obstacle of `type` for the point, by catalog weight – except obstacles with a roof to climb: where one
   * cannot stand, its share of the pick is owed (the other obstacles of the type take the spot); where it can,
   * the owed share is added to its own (at most certainty). So a container keeps its catalog share of its type
   * in the long run and the share of every other obstacle stays as it was. `owed` is updated in place.
   */
  private pickDefinition(type: ObstacleType, point: ObstacleSpawnPoint, ctx: PopulateContext, owed: Record<string, number>): ObstacleDefinition | undefined {
    const all = this.deps.obstacles.randomOfType(type);
    const total = all.reduce((sum, d) => sum + d.weight, 0);
    const plain = all.filter((d) => !this.roofClimbOf(d) && this.fits(d, point, ctx.startDistance));
    const plainTotal = plain.reduce((sum, d) => sum + d.weight, 0);
    if (total <= 0) return undefined;
    // Only obstacles with a roof could stand here: they take the spot by weight (no share to hand over).
    if (plainTotal <= 0) return ctx.rng.weighted(all.filter((d) => this.fits(d, point, ctx.startDistance)).map((d) => [d, d.weight] as const));
    const entries: (readonly [ObstacleDefinition, number])[] = [];
    let roofs = 0;
    for (const d of all) {
      if (!this.roofClimbOf(d)) continue;
      const share = d.weight / total;
      if (this.fits(d, point, ctx.startDistance)) {
        const p = Math.min(Math.max(share + (owed[d.id] ?? 0), 0), 1 - roofs);
        owed[d.id] = (owed[d.id] ?? 0) - (p - share);
        roofs += p;
        entries.push([d, p]);
      } else {
        owed[d.id] = (owed[d.id] ?? 0) + share;
      }
    }
    for (const d of plain) entries.push([d, ((1 - roofs) * d.weight) / plainTotal]);
    return ctx.rng.weighted(entries);
  }

  /** Placed obstacles keep their lanes busy (from their front until a runner who got past is back on the road); containers reserve their drop zone. */
  private noteObstacles(placed: readonly PlacedObstacle[], startDistance: number): void {
    this.runUps = this.runUps
      .with(placed.map((p) => obstacleStretch(p.definition, p.point.lane, startDistance + p.point.position.z, this.layout)))
      .reserving(
        placed.flatMap((p) => {
          const climb = this.roofClimbOf(p.definition);
          return climb ? [dropZoneStretch(p.definition, climb, p.point.lane, startDistance + p.point.position.z)] : [];
        }),
      );
  }

  // ── Obstacles ────────────────────────────────────────────────────────────

  private placeObstacles(definition: SegmentDefinition, ctx: PopulateContext): ObstacleLayout {
    const terrain = this.terrainOf(definition);
    const slots: Slot[] = terrain.rows.map((row) => ({ z: row.z, terrain: row.lanes, mode: 'terrain', points: [], fixed: [] }));
    const wallEnds: Slot[] = terrain.wallStretches
      .filter((stretch) => stretch.zTo < definition.length)
      .map((stretch) => ({ z: stretch.zTo, terrain: null, mode: 'wallEnd', points: [], fixed: [] }));
    const allHazards = (definition.hazards ?? []).map((h) => h.id);
    let hazards: readonly string[] = [];
    let lines: readonly string[] | undefined;
    if (!ctx.safe) {
      hazards = allHazards;
      if (definition.obstacleVariants && definition.obstacleVariants.length > 0) {
        const variant = this.pickVariant(definition.obstacleVariants, ctx);
        if (variant) this.addDesignedRows(slots, definition, variant.placements, 'variant');
        hazards = variant?.hazards ?? allHazards;
        lines = variant?.collectibleLines;
      } else if (definition.fixedObstacles) {
        this.addDesignedRows(slots, definition, definition.fixedObstacles, 'setPiece');
      } else {
        this.addRandomRows(slots, definition, ctx);
      }
      this.addHazardRows(slots, definition, hazards);
      // The ground the moving hazards need is no run-up either (conservative: also if a hazard is left out below).
      this.runUps = this.runUps.with(this.hazardBusyOf(definition, hazards).map((s) => shifted(s, ctx.startDistance)));
    }
    const ordered = [...wallEnds, ...slots].sort((a, b) => a.z - b.z);

    const dropped = new Set<string>();
    const placed: PlacedObstacle[] = [];
    const place = (row: RolledRow) => {
      placed.push(...row.obstacles);
      this.noteObstacles(row.obstacles, ctx.startDistance);
      this.owedRoofs = row.owed;
    };
    for (const slot of ordered) {
      const distance = ctx.startDistance + slot.z;
      switch (slot.mode) {
        case 'wallEnd':
          this.history = SequenceValidator.wallsEnd(this.history, distance);
          break;
        case 'random':
          place(this.rollSlot(slot, definition, distance, ctx));
          break;
        case 'setPiece': {
          // Hand-designed set piece (e.g. collapse debris): always placed – the rows are guarded by
          // unit tests at max speed – but still recorded so following segments respect them.
          const row = this.designedRow(slot, ctx);
          this.history = this.deps.validator.validate({ distance, lanes: this.slotLanes(slot, definition, row.obstacles) }, this.history).history;
          place(row);
          break;
        }
        case 'variant': {
          const row = this.designedRow(slot, ctx);
          const result = this.deps.validator.validate({ distance, lanes: this.slotLanes(slot, definition, row.obstacles) }, this.history);
          if (result.ok) {
            this.history = result.history;
            place(row);
          } else {
            this.recordTerrain(slot, distance); // the line does not fit the track before it – leave its obstacles out
          }
          break;
        }
        case 'terrain':
          this.recordTerrain(slot, distance);
          break;
        case 'hazard': {
          // A moving hazard whose key line does not fit the track before it – or that would move through the
          // drop zone behind a container – stays away.
          const hazard = slot.hazard;
          if (!hazard || dropped.has(hazard.id)) break;
          if (this.hazardBusyOf(definition, [hazard.id]).some((s) => this.runUps.isReserved(shifted(s, ctx.startDistance)))) {
            dropped.add(hazard.id);
            break;
          }
          const result = this.deps.validator.validate({ distance, lanes: hazard.lanes }, this.history);
          if (result.ok) this.history = result.history;
          else dropped.add(hazard.id);
          break;
        }
      }
    }
    return { placed, hazards: hazards.filter((id) => !dropped.has(id)), lines };
  }

  /**
   * One hand-designed layout, weighted – only layouts made for the difficulty tier at the segment
   * start (ObstacleVariant.rowLayout); all of them if none is.
   */
  private pickVariant(variants: readonly ObstacleVariant[], ctx: PopulateContext): ObstacleVariant | undefined {
    const rowLayout = this.deps.difficulty.tierAt(ctx.startDistance).rowLayout;
    const fitting = variants.filter((v) => v.rowLayout === undefined || v.rowLayout === rowLayout);
    return ctx.rng.weighted((fitting.length > 0 ? fitting : variants).map((v) => [v, v.weight] as const));
  }

  /** The designed obstacles of a line – those that cannot stand there swapped or left out (designedObstacle). */
  private designedRow(slot: Slot, ctx: PopulateContext): RolledRow {
    const owed = { ...this.owedRoofs };
    return { obstacles: slot.fixed.flatMap((p) => this.designedObstacle(p, ctx, owed) ?? []), owed };
  }

  /** What the active moving hazards keep busy, per lane (segment-local). */
  private hazardBusyOf(definition: SegmentDefinition, ids: readonly string[]): LaneStretch[] {
    const config = this.deps.hazards ?? HAZARDS_CONFIG;
    return ids.flatMap((id) => {
      const hazard = definition.hazards?.find((h) => h.id === id);
      return hazard ? hazardStretches(hazard, config, definition.lanes.length, this.layout) : [];
    });
  }

  /** Key lines of the active moving hazards – decision rows of their own (kept apart from obstacle rows by the validator). */
  private addHazardRows(slots: Slot[], definition: SegmentDefinition, ids: readonly string[]): void {
    const laneCount = definition.lanes.length;
    for (const id of ids) {
      const hazard = definition.hazards?.find((h) => h.id === id);
      if (!hazard) continue;
      const byZ = new Map<number, (ObstacleType | null)[]>();
      for (const requirement of hazardRequirements(hazard, laneCount)) {
        const lanes = byZ.get(requirement.z) ?? Array.from({ length: laneCount }, () => null);
        lanes[requirement.lane] = requirement.type;
        byZ.set(requirement.z, lanes);
      }
      for (const [z, lanes] of byZ) slots.push({ z, terrain: null, mode: 'hazard', points: [], fixed: [], hazard: { id, lanes } });
    }
  }

  /** Random rows of the difficulty tier's row layout, joined to terrain lines where they coincide. */
  private addRandomRows(slots: Slot[], definition: SegmentDefinition, ctx: PopulateContext): void {
    const tier = this.deps.difficulty.tierAt(ctx.startDistance);
    const rows = definition.obstacleRowLayouts[tier.rowLayout]
      .map((row) => definition.obstacleSpawnPoints.filter((p) => p.row === row))
      .filter((points) => points.length > 0);
    for (const points of rows) {
      const slot = this.slotAt(slots, points[0]?.position.z ?? 0);
      slot.mode = 'random';
      slot.points = points;
    }
  }

  private addDesignedRows(slots: Slot[], definition: SegmentDefinition, placements: readonly FixedObstaclePlacement[], mode: SlotMode): void {
    const byRow = new Map<string, PlacedObstacle[]>();
    for (const placement of placements) {
      const point = definition.obstacleSpawnPoints.find((p) => p.id === placement.spawnPointId);
      if (point) byRow.set(point.row, [...(byRow.get(point.row) ?? []), { point, definition: this.deps.obstacles.get(placement.obstacleId) }]);
    }
    for (const items of byRow.values()) {
      const slot = this.slotAt(slots, items[0]?.point.position.z ?? 0);
      slot.mode = mode;
      slot.fixed = [...slot.fixed, ...items];
    }
  }

  /** The terrain line within merge distance of z, or a new obstacle-only line. */
  private slotAt(slots: Slot[], z: number): Slot {
    const terrainSlots = slots.filter((s) => s.terrain !== null && s.mode === 'terrain');
    const index = terrainRowIndexAt(
      terrainSlots.map((s) => ({ z: s.z, lanes: s.terrain ?? [] })),
      z,
      this.deps.level.terrain.rowMergeDistance,
    );
    const existing = terrainSlots[index];
    if (existing) return existing;
    const slot: Slot = { z, terrain: null, mode: 'terrain', points: [], fixed: [] };
    slots.push(slot);
    return slot;
  }

  private rollSlot(slot: Slot, definition: SegmentDefinition, distance: number, ctx: PopulateContext): RolledRow {
    const { difficulty, level } = this.deps;
    const tier = difficulty.tierAt(ctx.startDistance);
    if (ctx.rng.chance(tier.rowFillChance)) {
      for (let attempt = 0; attempt < level.maxLayoutAttempts; attempt++) {
        // the tier's minimum per row – relaxed to 1 in the later attempts when no fair row comes up with it
        const min = attempt < level.maxLayoutAttempts / 2 ? tier.minObstaclesPerRow : 1;
        const candidate = this.rollRow(slot.points, min, tier.maxObstaclesPerRow, tier.obstacleWeights, ctx);
        if (candidate.obstacles.length === 0) break;
        const result = this.deps.validator.validate({ distance, lanes: this.slotLanes(slot, definition, candidate.obstacles) }, this.history);
        if (result.ok) {
          this.history = result.history;
          return candidate;
        }
      }
    }
    this.recordTerrain(slot, distance);
    return { obstacles: [], owed: this.owedRoofs };
  }

  /** Terrain is geometry – it always enters the history, playable or not. */
  private recordTerrain(slot: Slot, distance: number): void {
    if (slot.terrain) this.history = this.deps.validator.validate({ distance, lanes: slot.terrain }, this.history).history;
  }

  private slotLanes(slot: Slot, definition: SegmentDefinition, obstacles: readonly PlacedObstacle[]): (ObstacleType | null)[] {
    const lanes: (ObstacleType | null)[] = definition.lanes.map(() => null);
    for (const p of obstacles) lanes[p.point.lane] = p.definition.type;
    return slot.terrain ? mergeRowLanes(slot.terrain, lanes) : lanes;
  }

  private rollRow(
    points: readonly ObstacleSpawnPoint[],
    minPerRow: number,
    maxPerRow: number,
    weights: Readonly<Partial<Record<ObstacleType, number>>>,
    ctx: PopulateContext,
  ): RolledRow {
    const rng = ctx.rng;
    // A type is possible where one of its obstacles can stand (a container needs its run-up and drop zone, nothing stands in a drop zone).
    const possible = (point: ObstacleSpawnPoint) =>
      point.allowedTypes.filter((t) => this.deps.obstacles.randomOfType(t).some((d) => this.fits(d, point, ctx.startDistance)));
    // Lanes in the drop zone of a container before them stay free – the row picks among the others.
    const usable = points.filter((point) => possible(point).length > 0);
    const owed = { ...this.owedRoofs };
    if (usable.length === 0) return { obstacles: [], owed };
    const most = Math.min(maxPerRow, usable.length);
    const count = rng.int(Math.min(Math.max(1, minPerRow), most), most);
    const lanes = rng.shuffle([...usable]).slice(0, count);
    const types = lanes.map((point) => rng.weighted(possible(point).map((t) => [t, weights[t] ?? 0] as const)));
    this.preferRoofSpots(lanes, types, usable, possible, owed, ctx);
    const obstacles: PlacedObstacle[] = [];
    lanes.forEach((point, i) => {
      const type = types[i];
      const definition = type ? this.pickDefinition(type, point, ctx, owed) : undefined;
      if (definition) obstacles.push({ point, definition });
    });
    return { obstacles, owed };
  }

  /**
   * While an obstacle with a roof to climb is behind its share (owed > 0), a type of it rolled where it cannot
   * stand moves within the row to a lane where it can – an unused lane of the row, or one whose rolled type is
   * swapped. The row keeps its types and its count; only which lane gets what changes.
   */
  private preferRoofSpots(
    lanes: ObstacleSpawnPoint[],
    types: (ObstacleType | undefined)[],
    usable: readonly ObstacleSpawnPoint[],
    possible: (point: ObstacleSpawnPoint) => ObstacleType[],
    owed: Readonly<Record<string, number>>,
    ctx: PopulateContext,
  ): void {
    const start = ctx.startDistance;
    for (const roof of this.deps.obstacles.all()) {
      if (roof.weight <= 0 || !this.roofClimbOf(roof) || (owed[roof.id] ?? 0) <= 0) continue;
      const takes = (point: ObstacleSpawnPoint) => this.fits(roof, point, start) && possible(point).includes(roof.type);
      for (let i = 0; i < lanes.length; i++) {
        const here = lanes[i]!;
        if (types[i] !== roof.type || this.fits(roof, here, start)) continue;
        const unused = usable.filter((p) => !lanes.includes(p) && takes(p));
        if (unused.length > 0) {
          lanes[i] = ctx.rng.pick(unused);
          continue;
        }
        const j = lanes.findIndex((p, k) => {
          const other = types[k];
          return k !== i && other !== undefined && other !== roof.type && takes(p) && possible(here).includes(other);
        });
        if (j >= 0) [types[i], types[j]] = [types[j], types[i]];
      }
    }
  }

  // ── Collectibles & decoration ────────────────────────────────────────────

  private placeCollectibles(
    definition: SegmentDefinition,
    placed: readonly PlacedObstacle[],
    hazards: readonly string[],
    ctx: PopulateContext,
    lines?: readonly string[],
  ): CollectiblePlacement[] {
    const { level, collectibles } = this.deps;
    const rng = ctx.rng;
    const footprints = this.hazardFootprints(definition, hazards);
    // Fewer bottles and bonus pickups with every difficulty tier (DifficultyTier.bottleShare / bonusShare) – the chances
    // are drawn either way, so the random stream of everything else stays the same.
    const tier = this.deps.difficulty.tierAt(ctx.startDistance);
    // The run start always shows its line; the free module behind a junction (≈ 4 per km) follows the tier's share too.
    const lineChance = ctx.safe ? (ctx.afterJunction ? tier.bottleShare : 1) : level.collectibleLineChance * tier.bottleShare;
    const line = rng.chance(lineChance) ? this.placeLine(definition, placed, footprints, ctx, lines) : [];
    // A roof line takes its stretch of the lane – the line's bottles there would hang right below it.
    const roofs = this.placeRoofLines(placed, footprints, ctx, tier.bottleShare);
    const kept = line.filter((bottle) => !roofs.some((roof) => withinLine(bottle, roof, level.roofRoutes.bottleSpacing / 2)));
    const result = kept.map((b) => b.placement);

    if (result.length > 0 && !ctx.safe && rng.chance(level.bonusCollectibleChance * tier.bonusShare)) {
      const index = rng.int(0, result.length - 1);
      const bonusType = rng.weighted(Object.entries(level.bonusMix).map(([type, weight]) => [type as CollectibleType, weight ?? 0] as const)) ?? CollectibleType.Toolbox;
      const original = result[index];
      if (original) result[index] = { ...original, collectibleId: collectibles.byType(bonusType).id };
    }
    if (!ctx.safe && ctx.startDistance >= level.turboCrates.fromDistance) this.placeTurboCrate(kept, result, roofs, ctx);
    return [...result, ...roofs.flat().map((b) => b.placement)];
  }

  /**
   * Beer crate (turbo) only where it is hard to reach (LevelConfig.turboCrates): the last bottle on a container roof,
   * else the highest bottle of a high designed line. At most one per segment; a bonus already placed stays.
   */
  private placeTurboCrate(line: readonly PlacedBottle[], result: CollectiblePlacement[], roofs: PlacedBottle[][], ctx: PopulateContext): void {
    const config = this.deps.level.turboCrates;
    const bottle = this.deps.collectibles.byType(CollectibleType.BeerBottle).id;
    const crate = this.deps.collectibles.byType(CollectibleType.BeerCrate).id;
    // Own random stream per segment position: the crates never shift the draws of the track (obstacles, bottles).
    const rng = new Random(Random.deriveSeed(Random.deriveSeed(ctx.rng.seed, TURBO_CRATE_STREAM), Math.round(ctx.startDistance)));
    const roof = roofs.find((r) => r.length > 0);
    if (roof && rng.chance(config.roofChance)) {
      const last = roof[roof.length - 1];
      if (last) roof[roof.length - 1] = { ...last, placement: { ...last.placement, collectibleId: crate } };
      return;
    }
    let highest: PlacedBottle | null = null;
    for (const placedBottle of line) {
      const y = placedBottle.point.position.y;
      if (y >= config.minHeight && (!highest || y > highest.point.position.y)) highest = placedBottle;
    }
    if (!highest || !rng.chance(config.highLineChance)) return;
    const id = highest.placement.spawnPointId;
    const index = result.findIndex((p) => p.spawnPointId === id && p.collectibleId === bottle);
    const original = result[index];
    if (original) result[index] = { ...original, collectibleId: crate };
  }

  /** The bottles of the segment's line (arcs over jump obstacles, none inside obstacles or a hazard's way). */
  private placeLine(
    definition: SegmentDefinition,
    placed: readonly PlacedObstacle[],
    footprints: readonly CollectibleFootprint[],
    ctx: PopulateContext,
    lines?: readonly string[],
  ): PlacedBottle[] {
    const points = this.pickCollectibleLine(definition, ctx, lines);
    const bottle = this.deps.collectibles.byType(CollectibleType.BeerBottle).id;
    const result: PlacedBottle[] = [];

    for (const point of points) {
      let heightOffset = 0;
      let blocked = false;
      for (const obstacle of placed) {
        if (obstacle.point.lane !== point.lane) continue;
        const halfDepth = ObstacleCatalog.depthOf(obstacle.definition) / 2;
        const dz = Math.abs(point.position.z - obstacle.point.position.z);
        if (obstacle.definition.type === ObstacleType.Jump) {
          // Bottles form an arc over jump obstacles – they show the ideal line.
          heightOffset = Math.max(heightOffset, this.arcOffset(dz, halfDepth));
        } else if (obstacle.definition.type !== ObstacleType.Slide && dz < halfDepth + COLLECTIBLE_CLEARANCE) {
          blocked = true;
        }
      }
      // Moving hazards: no bottles inside a load or in a vehicle's way; an arc over the rubble heap.
      for (const footprint of footprints) {
        if (footprint.lane !== point.lane) continue;
        const z = point.position.z;
        if (footprint.mode === 'blocked' && z >= footprint.zFrom && z <= footprint.zTo) blocked = true;
        if (footprint.mode === 'arc') {
          const halfDepth = (footprint.zTo - footprint.zFrom) / 2;
          heightOffset = Math.max(heightOffset, this.arcOffset(Math.abs(z - (footprint.zFrom + halfDepth)), halfDepth));
        }
      }
      if (!blocked) result.push({ point, placement: { spawnPointId: point.id, collectibleId: bottle, heightOffset } });
    }
    return result;
  }

  /**
   * Bottle lines onto the roofs of the placed containers (LevelConfig.roofRoutes.bottleChance each): up the
   * ideal double jump and along the roof (roofLines.ts). Their run-up is free by construction (fitsRunUp), so
   * no bottle hangs in an obstacle; none where a moving hazard needs the lane either.
   */
  private placeRoofLines(placed: readonly PlacedObstacle[], footprints: readonly CollectibleFootprint[], ctx: PopulateContext, share = 1): PlacedBottle[][] {
    const config = this.deps.level.roofRoutes;
    const bottle = this.deps.collectibles.byType(CollectibleType.BeerBottle).id;
    const lines: PlacedBottle[][] = [];
    for (const obstacle of placed) {
      const climb = this.roofClimbOf(obstacle.definition);
      if (!climb || !ctx.rng.chance(config.bottleChance * share)) continue;
      const speed = this.deps.difficulty.speedAt(ctx.startDistance + obstacle.point.position.z);
      const points = roofLinePoints(obstacle.point, obstacle.definition, climb, this.layout, config, speed).filter(
        (point) => !footprints.some((f) => f.mode === 'blocked' && f.lane === point.lane && point.position.z >= f.zFrom && point.position.z <= f.zTo),
      );
      // The points travel with the placement – the module has no sockets up there.
      if (points.length > 0) lines.push(points.map((point) => ({ point, placement: { spawnPointId: point.id, collectibleId: bottle, heightOffset: 0, point } })));
    }
    return lines;
  }

  /** Extra bottle height `dz` from the centre of a jump obstacle (parabolic arc over it). */
  private arcOffset(dz: number, halfDepth: number): number {
    const arcHalfLength = halfDepth + JUMP_ARC_RUN_UP;
    return dz < arcHalfLength ? this.deps.level.collectibleJumpArcHeight * (1 - (dz / arcHalfLength) ** 2) : 0;
  }

  private hazardFootprints(definition: SegmentDefinition, ids: readonly string[]): CollectibleFootprint[] {
    const config = this.deps.hazards ?? HAZARDS_CONFIG;
    return ids.flatMap((id) => {
      const hazard = definition.hazards?.find((h) => h.id === id);
      return hazard ? collectibleFootprints(hazard, config, COLLECTIBLE_CLEARANCE) : [];
    });
  }

  /**
   * The points of one collectible line: a designed line (zig-zag, over a pit, along a
   * platform) if the segment defines lines, otherwise one whole lane. Safe segments
   * (run start) take the first line / the center lane. `suited`: the lines the obstacle
   * variant asks for (e.g. its risk line) – any line if none of them exists.
   */
  private pickCollectibleLine(definition: SegmentDefinition, ctx: PopulateContext, suited?: readonly string[]): CollectibleSpawnPoint[] {
    const all = this.linesOf(definition);
    const matching = suited ? all.filter((line) => suited.includes(line)) : all;
    const lines = matching.length > 0 ? matching : all;
    if (lines.length > 0) {
      const line = ctx.safe ? lines[0] : lines[ctx.rng.int(0, lines.length - 1)];
      return definition.collectibleSpawnPoints.filter((p) => p.line === line);
    }
    const centerLane = Math.floor(definition.lanes.length / 2);
    const lane = ctx.safe ? centerLane : ctx.rng.int(0, definition.lanes.length - 1);
    // Points of set-piece rewards (fixedCollectibles) are no bottle spots.
    const fixed = new Set((definition.fixedCollectibles ?? []).map((f) => f.spawnPointId));
    return definition.collectibleSpawnPoints.filter((p) => p.lane === lane && !fixed.has(p.id));
  }

  private linesOf(definition: SegmentDefinition): readonly string[] {
    let lines = this.collectibleLines.get(definition.id);
    if (!lines) {
      lines = [...new Set(definition.collectibleSpawnPoints.flatMap((p) => (p.line === undefined ? [] : [p.line])))];
      this.collectibleLines.set(definition.id, lines);
    }
    return lines;
  }

  private placeDecorations(definition: SegmentDefinition, ctx: PopulateContext): DecorationPlacement[] {
    const result: DecorationPlacement[] = [];
    const rng = ctx.cosmeticRng;
    const distance = ctx.startDistance;
    const weightOf = this.deps.decorationWeight;
    const clearance = ctx.afterJunction ? this.deps.level.junctions.roadsideClearance : -Infinity;
    for (const socket of definition.decorationSockets) {
      const bay = socket.tags.includes(ROADSIDE_TAG);
      if (bay && socket.position.z - socket.maxFootprint.width / 2 < clearance) continue;
      if (!rng.chance(socket.fillChance ?? this.deps.level.decorationFillChance)) continue;
      const fitting = this.deps.decorations.filter(
        (d) =>
          d.footprint.width <= socket.maxFootprint.width &&
          d.footprint.depth <= socket.maxFootprint.depth &&
          d.requiredSocketTags.every((tag) => socket.tags.includes(tag)),
      );
      // Neighbouring bays on one side never repeat the same asset (cosmetic variety) – unless the zone
      // favours it (night: lit yards may follow each other).
      const previous = bay ? this.lastBayAsset.get(socket.side) : undefined;
      const repeat = previous === undefined || fitting.length < 2 || (weightOf?.(previous, distance) ?? 1) > 1;
      const varied = repeat ? fitting : fitting.filter((d) => d.assetId !== previous);
      const picked = rng.weighted(varied.map((d) => [d, d.weight * (weightOf?.(d.assetId, distance) ?? 1)] as const));
      if (!picked) continue;
      if (bay) this.lastBayAsset.set(socket.side, picked.assetId);
      const jitter = picked.yawJitter ? rng.range(-picked.yawJitter, picked.yawJitter) : 0;
      const yawOffset = picked.yaw || jitter ? (picked.yaw ?? 0) + jitter : undefined;
      result.push({ socketId: socket.id, assetId: picked.assetId, yawOffset, spacing: picked.spacing });
    }
    return result;
  }
}

/** A segment-local lane stretch moved to run distance. */
function shifted(stretch: LaneStretch, startDistance: number): LaneStretch {
  return { lane: stretch.lane, zFrom: startDistance + stretch.zFrom, zTo: startDistance + stretch.zTo };
}

/** The bottle stands in the lane of `line`, within its stretch (± `margin`). */
function withinLine(bottle: PlacedBottle, line: readonly PlacedBottle[], margin: number): boolean {
  const zs = line.filter((b) => b.point.lane === bottle.point.lane).map((b) => b.point.position.z);
  if (zs.length === 0) return false;
  const z = bottle.point.position.z;
  return z >= Math.min(...zs) - margin && z <= Math.max(...zs) + margin;
}

/** Random stream of the optional set-piece rewards (`FixedCollectiblePlacement.chance`) – the track's draws stay as they are. */
const FIXED_REWARD_STREAM = 0x3e7a11;

/**
 * Set-piece rewards (the golden bottle): on top of the random line, no line chance. A reward with `chance` (the beer
 * crate on the container tower) is drawn from its own stream per segment position; without it it is always placed.
 */
function fixedCollectiblesOf(definition: SegmentDefinition, ctx: PopulateContext): CollectiblePlacement[] {
  const rng = new Random(Random.deriveSeed(Random.deriveSeed(ctx.rng.seed, FIXED_REWARD_STREAM), Math.round(ctx.startDistance)));
  return (definition.fixedCollectibles ?? [])
    .filter((f) => (f.chance === undefined || rng.chance(f.chance)) && ctx.startDistance >= (f.minDistance ?? 0))
    .map((f) => ({ spawnPointId: f.spawnPointId, collectibleId: f.collectibleId, heightOffset: 0, scale: f.scale }));
}
